import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { orderDetails } from "../../../../../services/orderService";
import Badge from "../../../../elements/Badge";
import Button from "../../../../form/Button";
import { lngDetect } from "../../../../../helpers/lngDetect";
import Table from "../../../../elements/table/Table";

const SaleDetails = ({ data }) => {
  const [selectedItems, setSelectedItems] = useState([]);

  // Fetch order details
  const {
    data: orderData,
    isLoading: orderLoading,
    isFetching,
  } = useQuery({
    queryKey: ["order-details", data?._id],
    queryFn: async () => {
      const res = await orderDetails({ id: data?._id });
      if (res?.status !== 200) {
        toast.warning(res?.message);
        return null;
      }
      return res?.data;
    },
    enabled: !!data?._id,
  });

  const getPaymentMethodIcon = (method) => {
    switch (method?.toLowerCase()) {
      case "card":
        return "fa-regular fa-credit-card";
      case "paypal":
        return "fa-brands fa-paypal";
      case "bank":
        return "fa-regular fa-building-columns";
      default:
        return "fa-regular fa-wallet";
    }
  };

  if (!data) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-gray-500">No order selected</p>
      </div>
    );
  }

  if (orderLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!orderData) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-gray-500">Order not found</p>
      </div>
    );
  }

  const {
    orderId: orderNumber,
    total,
    payment,
    status,
    createdAt,
    promoterInfo,
    eventData,
    userData,
    ticketCount,
    orderItemPromotionData = [],
  } = orderData;

  // Fixed calculate overall commission
  const calculateOverallCommission = () => {
    if (!orderItemPromotionData || orderItemPromotionData.length === 0) {
      return 0;
    }

    return orderItemPromotionData.reduce((total, item) => {
      const commission = item?.commission;
      const price = parseFloat(item?.price || 0);

      if (!commission || price === 0) return total;

      if (commission.type === "fixed") {
        return total + (commission.value || 0);
      } else if (commission.type === "percentage") {
        // Assuming commission.value is already in decimal form (0.05 = 5%)
        return total + price * (commission.value || 0);
      }

      return total;
    }, 0);
  };

  const overallCommission = calculateOverallCommission();

  // Table Header Configuration
  const tableHeadData = [
    {
      _id: "ticketType",
      name: "Ticket Name",
      align: "left",
    },
    {
      _id: "price",
      name: "Price",
      align: "left",
    },
    {
      _id: "discount",
      name: "Discount",
      align: "left",
    },
    {
      _id: "commission",
      name: "Commission",
      align: "left",
    },
  ];

  // Fixed discount display function
  const getDiscountDisplay = (discount, price) => {
    if (!discount) return "-";

    const priceValue = parseFloat(price || 0);

    if (discount.type === "fixed") {
      return `$${parseFloat(discount.value || 0).toFixed(2)}`;
    } else if (discount.type === "percentage") {
      const discountAmount = priceValue * (discount.value || 0);
      return `${((discount.value || 0) * 100).toFixed(
        1
      )}% ($${discountAmount.toFixed(2)})`;
    }
    return "-";
  };

  // Fixed commission display function
  const getCommissionDisplay = (commission, price) => {
    if (!commission) return "-";

    const priceValue = parseFloat(price || 0);

    if (commission.type === "fixed") {
      return `$${parseFloat(commission.value || 0).toFixed(2)}`;
    } else if (commission.type === "percentage") {
      const commissionAmount = priceValue * (commission.value || 0);
      return `${((commission.value || 0) * 100).toFixed(
        1
      )}% ($${commissionAmount.toFixed(2)})`;
    }
    return "-";
  };

  // Table Body Data
  const tableData =
    orderItemPromotionData?.map((item) => {
      const ticketStatus = item?.status || "active";
      const ticketStatusText =
        ticketStatus.charAt(0)?.toUpperCase() + ticketStatus.slice(1);

      return {
        _id: item?._id || item?.ticketId,
        rowData: [
          {
            _id: "ticketType",
            type: "text",
            align: "left",
            cellClass: "!min-w-[150px]",
            data: (
              <span className="text-sm">
                {lngDetect(item?.name) ||
                  item?.ticketType?.name ||
                  item?.ticketName ||
                  "General"}
              </span>
            ),
          },
          {
            _id: "price",
            type: "text",
            align: "left",
            cellClass: "!min-w-[100px]",
            data: (
              <span className="text-sm font-semibold">
                ${parseFloat(item?.price || item?.amount || 0).toFixed(2)}
              </span>
            ),
          },
          {
            _id: "discount",
            type: "text",
            align: "left",
            cellClass: "!min-w-[100px]",
            data: (
              <span className="text-sm text-orange-600 font-medium">
                {getDiscountDisplay(item?.discount, item?.price)}
              </span>
            ),
          },
          {
            _id: "commission",
            type: "text",
            align: "left",
            cellClass: "!min-w-[120px]",
            data: (
              <span className="text-sm text-green-600 font-medium">
                {getCommissionDisplay(item?.commission, item?.price)}
              </span>
            ),
          },
        ],
      };
    }) || [];

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header Section */}
      <div className="bg-gray-50 rounded-lg p-6 border">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-500">
              Order ID
            </label>
            <p className="text-lg font-semibold text-gray-900 whitespace-nowrap">
              {orderNumber}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">
              Total Amount
            </label>
            <p className="text-lg font-semibold text-green-600">
              ${parseFloat(total || 0).toFixed(2)}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">
              Overall Commission
            </label>
            <p className="text-lg font-semibold text-blue-600">
              ${overallCommission.toFixed(2)}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">
              Order Date
            </label>
            <p className="text-lg font-medium text-gray-900">
              {dayjs(createdAt).format("MMM DD, YYYY HH:mm")}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Customer Information */}
        <div className="bg-white border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <i className="fa-regular fa-user mr-2 text-blue-500"></i>
            Customer Information
          </h3>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-500">Name</label>
              <p className="text-base font-medium text-gray-900">
                {userData?.firstName || ""} {userData?.lastName || ""}
              </p>
            </div>
            {/* <div>
              <label className="text-sm font-medium text-gray-500">Email</label>
              <p className="text-sm text-gray-700">{userData?.email || "-"}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Phone</label>
              <p className="text-sm text-gray-700">{userData?.phone || "-"}</p>
            </div> */}
          </div>
        </div>

        {/* Event Information */}
        <div className="bg-white border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <i className="fa-regular fa-calendar mr-2 text-purple-500"></i>
            Event Information
          </h3>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-500">
                Event Name
              </label>
              <p className="text-base text-gray-900 font-medium">
                {lngDetect(eventData?.title) || "-"}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">
                Event Dates
              </label>
              <p className="text-sm font-medium text-gray-700">
                {eventData?.startDate
                  ? `${dayjs(eventData.startDate).format(
                      "MMM DD, YYYY"
                    )} - ${dayjs(eventData.endDate).format("MMM DD, YYYY")}`
                  : "-"}
              </p>
            </div>
            {/* <div>
              <label className="text-sm font-medium text-gray-500">
                Location
              </label>
              <p className="text-sm font-medium text-gray-700">
                {eventData?.address?.city
                  ? `${eventData.address.city}, ${eventData.address.state}, ${eventData.address.country}`
                  : "-"}
              </p>
            </div> */}
          </div>
        </div>
      </div>

      {/* Payment Information */}
      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <i className="fa-regular fa-credit-card mr-2 text-green-500"></i>
          Payment Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-500">
              Payment Method
            </label>
            <div className="flex items-center mt-1">
              <i
                className={`${getPaymentMethodIcon(
                  payment?.method
                )} mr-2 text-gray-600`}
              ></i>
              <span className="text-base font-medium text-gray-900 capitalize">
                {payment?.method || "-"}
              </span>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">
              Payment Status
            </label>
            <div className="mt-1">
              <Badge label={payment?.status || "pending"} />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Gateway</label>
            <p className="text-base font-medium text-gray-900 capitalize">
              {payment?.gateway || "-"}
            </p>
          </div>
        </div>
      </div>

      {/* Order Status */}
      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <i className="fa-regular fa-clipboard-check mr-2 text-blue-500"></i>
          Order Status
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-500">
              Order Status
            </label>
            <div className="mt-1">
              <Badge label={status?.info || "pending"} />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">
              Status Date
            </label>
            <p className="text-sm font-medium text-gray-700">
              {status?.date
                ? dayjs(status.date).format("MMM DD, YYYY HH:mm")
                : "-"}
            </p>
          </div>
        </div>
      </div>

      {/* Promoter Information */}
      {promoterInfo && (
        <div className="bg-white border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <i className="fa-regular fa-user-tie mr-2 text-indigo-500"></i>
            Promoter Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-500">
                Promoter Name
              </label>
              <p className="text-base font-medium text-gray-900">
                {promoterInfo?.info?.displayName || "-"}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">
                Commission Earned
              </label>
              <p className="text-lg font-semibold text-green-600">
                ${parseFloat(promoterInfo?.promoterFee || 0).toFixed(2)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tickets Sold */}
      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <i className="fa-regular fa-ticket mr-2 text-orange-500"></i>
          Tickets Sold ({ticketCount || tableData?.length || 0} tickets)
        </h3>

        {/* Using the reusable Table component with proper structure */}
        <Table
          tableHeadData={tableHeadData}
          tableData={tableData}
          isLoder={orderLoading}
          pageCount={1} // Single page for order details
          currentPage={1}
          onPageChange={() => {}} // No pagination needed
          originalData={[...tableData]}
          setOriginalData={() => {}}
          handleOrdering={() => {}}
          isFetching={isFetching}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          tableActionDropdownContainer={"!w-48"}
          // Show empty state when no tickets
          emptyMessage="No tickets found for this order"
        />
      </div>
    </div>
  );
};

export default SaleDetails;
